/**
 * Runtime environment-flag reader.
 *
 * The production bundle bakes every `import.meta.env.VITE_*` value as a
 * `#{TOKEN}#` placeholder (see `.env.production`) that the deploy step replaces
 * per environment (same mechanism as the legacy Angular shell's
 * `#{enableSessionStorage}#` tokens).
 *
 * Boolean flags MUST be read through this helper instead of inline
 * `import.meta.env.VITE_X === 'true'`. Vite inlines the env read as a string
 * literal, so `"#{TOKEN}#" === 'true'` is a comparison of two constants that
 * the minifier constant-folds to `false` and then strips the placeholder from
 * the emitted code — BEFORE the deploy step can replace it. The flag is then
 * permanently off in every environment (this is why sessionStorage,
 * sendTokenForMCPClient and serviceNowRequestForm never took effect).
 *
 * Passing the raw value through these functions keeps the `#{TOKEN}#`
 * placeholder in the bundle: the value is interpreted via a runtime function
 * call that the minifier cannot evaluate at build time, so the token survives to
 * be replaced. Mirrors legacy
 * `JSON.parse(environment.featureFlagConfiguration.enableSessionStorage)`, which
 * is likewise parsed at runtime.
 *
 * Always call with the env value as the ARGUMENT from the consuming module, e.g.
 * `isEnvFlagTrue(import.meta.env.VITE_ENABLE_SESSION_STORAGE)` — never wrap the
 * env read inside this module, or same-module inlining would re-introduce the
 * fold.
 *
 * The deploy `replace_token` step substitutes the pipeline value verbatim,
 * INCLUDING any quotes the yml used: `value: 'true'` lands in the bundle as the
 * string `'true'` (with quotes), not bare `true`. `normalizeEnvValue` strips one
 * layer of surrounding quotes + whitespace so every reader is tolerant of
 * quoted, bare, or unreplaced (`#{TOKEN}#`) values.
 */

/** Trim whitespace and strip ONE layer of matching surrounding quotes. */
export function normalizeEnvValue(value: string | undefined): string {
  if (value == null) return '';
  const trimmed = value.trim();
  if (trimmed.length >= 2) {
    const first = trimmed[0];
    if ((first === "'" || first === '"') && trimmed[trimmed.length - 1] === first) {
      return trimmed.slice(1, -1).trim();
    }
  }
  return trimmed;
}

/** True when the (deploy-replaced) flag value is `true` (quote/case tolerant). */
export function isEnvFlagTrue(value: string | undefined): boolean {
  return normalizeEnvValue(value).toLowerCase() === 'true';
}

/** Numeric env value with fallback when missing / unreplaced / non-numeric. */
export function envNumber(value: string | undefined, fallback: number): number {
  const raw = normalizeEnvValue(value);
  if (raw === '') return fallback;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : fallback;
}

/** Comma-separated env value → trimmed, non-empty items. */
export function envList(value: string | undefined): string[] {
  return normalizeEnvValue(value)
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
}
